import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useMediaQueryContext } from '../../../contexts/MediaQueryContext'
import { Microphone } from '../../../hooks/useMicrophones'

interface IProps {
  isTalking: boolean
  isMuted: boolean
  userMicrophoneStream: MediaStream | null
  selectedMicrophone?: Microphone | null
}

export const Wave: React.FC<IProps> = ({
  isTalking,
  isMuted,
  userMicrophoneStream,
  selectedMicrophone,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const [isUserTalking, setIsUserTalking] = useState(false)
  const barArrayRef = useRef<number[]>([]) // Store the bar data
  const { isSm } = useMediaQueryContext()

  const connectAudio = useCallback(() => {
    if (!userMicrophoneStream) return
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)()
    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(userMicrophoneStream)
    source.connect(analyser)
    analyserRef.current = analyser
    drawMicrophoneWaveform(isMuted)
  }, [userMicrophoneStream, selectedMicrophone, isMuted])

  useEffect(() => {
    if (userMicrophoneStream) {
      connectAudio()
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [connectAudio, userMicrophoneStream, selectedMicrophone])

  const drawMicrophoneWaveform = (muted: boolean) => {
    if (!canvasRef.current || !analyserRef.current) return

    const canvas = canvasRef.current
    const canvasCtx = canvas.getContext('2d')
    const analyser = analyserRef.current

    if (!canvasCtx) return

    analyser.fftSize = 256
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const barWidth = 2 // Width of each bar
    const gap = 1 // Gap between bars
    const totalBars = Math.floor(canvas.width / (barWidth + gap)) // How many bars fit on the screen

    // Fill the bar array with initial zeros
    if (barArrayRef.current.length === 0) {
      barArrayRef.current = Array.from({ length: totalBars }, () => 0)
    }

    let talkingCooldown = 0
    const cooldownFrames = 30 // About 0.5 seconds at 60fps

    const draw = () => {
      // Clear the canvas for each frame
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height)

      canvasCtx.beginPath()
      canvasCtx.moveTo(0, canvas.height / 2)
      canvasCtx.lineTo(canvas.width, canvas.height / 2)
      canvasCtx.strokeStyle = '#ffffff'
      canvasCtx.stroke()

      if (!muted) {
        analyser.getByteFrequencyData(dataArray)

        const talkingThreshold = 6
        const requiredFrames = 3

        // Calculate average amplitude
        const averageAmplitude =
          dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
        const barHeight = (averageAmplitude / 255) * canvas.height * 0.8

        // Shift existing bars to the left
        barArrayRef.current.shift()
        barArrayRef.current.push(barHeight)

        // Count talking frames
        const talkingFrames = barArrayRef.current.filter(
          height => height > talkingThreshold,
        ).length

        let isUserTalkingNow = talkingFrames >= requiredFrames

        // Update cooldown
        if (isUserTalkingNow) {
          talkingCooldown = cooldownFrames
        } else if (talkingCooldown > 0) {
          talkingCooldown--
          isUserTalkingNow = true // Keep it true during cooldown
        }

        // Update talking state
        setIsUserTalking(isUserTalkingNow)

        // Set color for the entire waveform
        canvasCtx.fillStyle = '#ffffff'

        // Draw all bars
        for (let i = 0; i < barArrayRef.current.length; i++) {
          const x = i * (barWidth + gap)
          const height = barArrayRef.current[i]

          // Draw the bar on both top and bottom halves
          canvasCtx.fillRect(
            x,
            canvas.height / 2 - height / 2,
            barWidth,
            height,
          )
          canvasCtx.fillRect(
            x,
            canvas.height / 2 + height / 2,
            barWidth,
            -height,
          )
        }
      } else {
        // Reset the user talking state when muted
        setIsUserTalking(false)

        // Reset the bar array to zeros when muted
        barArrayRef.current.fill(0)
      }

      animationFrameRef.current = requestAnimationFrame(draw)
    }

    draw()
  }

  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = isSm ? 80 : 160
      canvasRef.current.height = 40
      drawMicrophoneWaveform(isMuted)
    }
  }, [isSm])

  const waveformStyle = {
    filter:
      !isMuted && isUserTalking && !isTalking
        ? 'brightness(0) saturate(100%) invert(48%) sepia(93%) saturate(1720%) hue-rotate(346deg) brightness(101%) contrast(103%)'
        : 'none',
    transition: 'filter 0.3s ease',
  }

  return (
    <div className="relative flex-auto">
      <div className="tc-waveform-middle flex">
        <div className="tc-waveform-wave" style={waveformStyle}>
          <canvas ref={canvasRef} height={40} className="waveform-canvas" />
        </div>
      </div>
    </div>
  )
}
